import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../api/auth';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import {
  Building2,
  Mail,
  Lock,
  AlertCircle,
  Loader2,
  ChevronRight,
  ArrowLeft,
  KeyRound,
} from 'lucide-react';
import OtpInput from '../components/OtpInput';

/**
 * Two-step email login (EMAIL_OTP_AUTH_PRD Phase 2):
 *
 *   step="email"    — collect email → call /auth/lookup
 *   step="password" — user has a password → show password field
 *   step="otp"      — no password OR user chose "Use OTP instead"
 *   step="orgPicker"— multi-org account (returned by /login or /otp/verify)
 *
 * Google + Microsoft SSO have been removed per §8 decision #1/#2.
 * Backend gates the OTP endpoints on AUTH_MODE — if `classic`, the OTP
 * path 404s and the UI falls back to password-only silently.
 */
function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { clearOrg } = useOrg();

  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState(''); // preview convenience (PREVIEW_OTP_ECHO)
  const [otpMode, setOtpMode] = useState(false); // true → user chose OTP even though password exists
  const [loginMethod, setLoginMethod] = useState('password'); // 'password' | 'otp' — decides which endpoint the org-picker calls
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [availableOrgs, setAvailableOrgs] = useState([]);

  // Wipe stale org cache on mount so the multi-org picker starts clean.
  useEffect(() => {
    try {
      localStorage.removeItem('currentOrgId');
      localStorage.removeItem('currentOrgSlug');
      clearOrg?.();
    } catch (_) { /* noop */ }
  }, [clearOrg]);

  // ─── Helpers ────────────────────────────────────────────────────────
  const resetTransient = () => { setError(''); setInfo(''); };

  const parseError = (err, fallback = 'Something went wrong.') => {
    if (err?.message) return err.message;
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(', ');
    if (detail?.message) return detail.message;
    return fallback;
  };

  const completeLogin = (response) => {
    login(response);
    // Honour `?next=<url>` so deep-link share URLs bounce back after auth.
    // Only accept same-origin paths (prevents open-redirect abuse).
    let nextUrl = '/dashboard';
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get('next');
      if (raw && raw.startsWith('/') && !raw.startsWith('//')) {
        nextUrl = raw;
      }
    } catch (_) { /* noop */ }
    window.location.href = nextUrl;
  };

  // ─── Step: email → detect password/OTP mode ────────────────────────
  const handleEmailContinue = async (e) => {
    e?.preventDefault();
    resetTransient();
    if (!email) { setError('Please enter your email.'); return; }
    setLoading(true);
    try {
      const info = await authAPI.lookup(email.trim().toLowerCase());
      // Helpful non-registered message — closed B2B UX per product decision.
      // Both `exists === false` AND `canUseOtp === false` gate this: the
      // backend returns both flags cleared for unknown emails.
      if (info.exists === false) {
        setError("This email isn't registered. Please contact your admin to get access.");
        setLoading(false);
        return;
      }
      if (info.hasPassword) {
        setStep('password');
      } else {
        setStep('otp');
        await requestOtpCode();
      }
    } catch (err) {
      setError(parseError(err, 'Could not reach the server. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  // ─── Step: password login ───────────────────────────────────────────
  const handlePasswordSubmit = async (e) => {
    e?.preventDefault();
    resetTransient();
    if (!password) { setError('Please enter your password.'); return; }
    setLoading(true);
    setLoginMethod('password');
    try {
      localStorage.removeItem('organization');
      const response = await authAPI.login({ email: email.trim().toLowerCase(), password });
      completeLogin(response);
    } catch (err) {
      if (err?.response?.status === 300) {
        const orgs = err.response.data?.detail?.organizations || [];
        if (Array.isArray(orgs) && orgs.length) {
          setAvailableOrgs(orgs);
          setStep('orgPicker');
          setLoading(false);
          return;
        }
      }
      setError(parseError(err, 'Invalid email or password.'));
    } finally {
      setLoading(false);
    }
  };

  const handleOrgPick = async (org) => {
    resetTransient();
    setLoading(true);
    try {
      // Route the org-pick through whichever endpoint got us here. Calling
      // the password endpoint on the OTP path (with an empty password) is
      // what caused the historic 401 "Invalid email or password" bug.
      let response;
      if (loginMethod === 'otp') {
        response = await authAPI.verifyOtp({
          email: email.trim().toLowerCase(),
          code,
          orgId: org.id,
        });
      } else {
        response = await authAPI.login({
          email: email.trim().toLowerCase(),
          password,
          orgId: org.id,
        });
      }
      localStorage.setItem('currentOrgId', org.id);
      if (org.slug) localStorage.setItem('currentOrgSlug', org.slug);
      completeLogin(response);
    } catch (err) {
      if (err?.response?.status === 410) {
        setError('Your code expired while picking an org. Please request a new code.');
        setStep('otp');
        setCode('');
      } else {
        setError(parseError(err, 'Login failed.'));
      }
      setLoading(false);
    }
  };

  // ─── Step: OTP request + verify ─────────────────────────────────────
  const requestOtpCode = async () => {
    resetTransient();
    setDevCode('');
    setCode('');
    try {
      const res = await authAPI.requestOtp(email.trim().toLowerCase(), 'login');
      if (res?._devCode) setDevCode(res._devCode); // preview only
      setInfo('We sent a 6-digit code to your email. It expires in 10 minutes.');
    } catch (err) {
      if (err?.response?.status === 429) {
        setError('Too many requests. Please wait a few minutes and try again.');
        return;
      }
      if (err?.response?.status === 404) {
        // Belt-and-suspenders: /lookup already gates this, but if a user
        // reached the OTP step some other way (e.g. deep-linked toggle),
        // bounce them back to email with the same helpful message.
        setError("This email isn't registered. Please contact your admin to get access.");
        setStep('email');
        return;
      }
      setError(parseError(err, 'Could not send code. Please try again.'));
    }
  };

  const handleOtpComplete = async (fullCode) => {
    resetTransient();
    setLoading(true);
    setLoginMethod('otp');
    try {
      const response = await authAPI.verifyOtp({
        email: email.trim().toLowerCase(),
        code: fullCode,
      });
      completeLogin(response);
    } catch (err) {
      if (err?.response?.status === 300) {
        const orgs = err.response.data?.detail?.organizations || [];
        if (Array.isArray(orgs) && orgs.length) {
          setAvailableOrgs(orgs);
          setStep('orgPicker');
          setLoading(false);
          return;
        }
      }
      if (err?.response?.status === 410) {
        setError('That code is invalid or expired. Please request a new one.');
      } else {
        setError(parseError(err, 'Incorrect code.'));
      }
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  // ─── Navigation between steps ───────────────────────────────────────
  const goBackToEmail = () => {
    setStep('email');
    setPassword('');
    setCode('');
    setDevCode('');
    setOtpMode(false);
    resetTransient();
  };

  const switchToOtpMode = async () => {
    setOtpMode(true);
    setStep('otp');
    await requestOtpCode();
  };

  const switchBackToPassword = () => {
    if (!otpMode) {
      // User was in OTP because they have no password — password path is
      // meaningless for them. Nudge back to OTP.
      return;
    }
    setStep('password');
    resetTransient();
  };

  // ─── Render ─────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50 px-4">
      <div className="max-w-md w-full space-y-6 bg-white p-8 rounded-2xl shadow-xl">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-gradient-to-br from-orange-500 to-amber-500 p-3 rounded-xl">
              <Building2 className="w-8 h-8 text-white" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Welcome back</h2>
          <p className="mt-2 text-gray-600">Sign in to your Elevante.AI account</p>
        </div>

        {/* Error / info alerts */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start" data-testid="login-error">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
        {info && !error && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800" data-testid="login-info">
            {info}
          </div>
        )}

        {/* ─── Step: email ────────────────────────────────────────── */}
        {step === 'email' && (
          <form onSubmit={handleEmailContinue} className="space-y-5" data-testid="login-step-email">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                  placeholder="you@example.com"
                  data-testid="input-email"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center py-3 px-4 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              data-testid="btn-continue-email"
            >
              {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Checking…</> : <>Continue <ChevronRight className="w-4 h-4 ml-1" /></>}
            </button>
          </form>
        )}

        {/* ─── Step: password ─────────────────────────────────────── */}
        {step === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="space-y-5" data-testid="login-step-password">
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <button type="button" onClick={goBackToEmail} className="inline-flex items-center gap-1 hover:text-slate-700" data-testid="btn-back-to-email">
                <ArrowLeft className="w-3.5 h-3.5" /> {email}
              </button>
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  autoFocus
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                  placeholder="••••••••"
                  data-testid="input-password"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 transition-all"
              data-testid="btn-login-password"
            >
              {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Signing in…</> : 'Sign in'}
            </button>
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={switchToOtpMode}
                className="inline-flex items-center gap-1 text-orange-600 hover:text-orange-700 font-medium"
                data-testid="btn-use-otp-instead"
              >
                <KeyRound className="w-4 h-4" /> Use email code instead
              </button>
              <Link to="/forgot-password" className="text-slate-600 hover:text-slate-800" data-testid="link-forgot-password">
                Forgot password?
              </Link>
            </div>
          </form>
        )}

        {/* ─── Step: OTP ──────────────────────────────────────────── */}
        {step === 'otp' && (
          <div className="space-y-5" data-testid="login-step-otp">
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <button type="button" onClick={goBackToEmail} className="inline-flex items-center gap-1 hover:text-slate-700" data-testid="btn-back-to-email">
                <ArrowLeft className="w-3.5 h-3.5" /> {email}
              </button>
            </div>
            <div>
              <p className="text-sm text-slate-700 mb-3">Enter the 6-digit code we emailed you.</p>
              <OtpInput
                value={code}
                onChange={setCode}
                onComplete={handleOtpComplete}
                disabled={loading}
                error={!!error}
                testIdPrefix="login-otp"
              />
              {devCode && (
                <p className="mt-3 text-xs text-slate-400" data-testid="dev-code-hint">
                  Dev code (preview only): <span className="font-mono">{devCode}</span>
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => handleOtpComplete(code)}
              disabled={loading || code.length !== 6}
              className="w-full flex justify-center py-3 px-4 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 transition-all"
              data-testid="btn-verify-otp"
            >
              {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Verifying…</> : 'Sign in'}
            </button>
            <div className="flex items-center justify-between text-sm">
              <button type="button" onClick={requestOtpCode} className="text-orange-600 hover:text-orange-700 font-medium" data-testid="btn-resend-otp">
                Resend code
              </button>
              {otpMode && (
                <button type="button" onClick={switchBackToPassword} className="text-slate-600 hover:text-slate-800" data-testid="btn-use-password-instead">
                  Use password instead
                </button>
              )}
            </div>
          </div>
        )}

        {/* ─── Step: multi-org picker ─────────────────────────────── */}
        {step === 'orgPicker' && (
          <div className="space-y-4" data-testid="login-org-picker">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-100 mb-3">
                <Building2 className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Choose your workspace</h3>
              <p className="text-sm text-gray-600 mt-1">
                You belong to {availableOrgs.length} organizations — pick which one to sign in to.
              </p>
            </div>
            <div className="space-y-2.5">
              {availableOrgs.map((org) => (
                <button
                  key={org.id}
                  onClick={() => handleOrgPick(org)}
                  disabled={loading}
                  data-testid={`login-org-option-${org.slug || org.id}`}
                  className="group w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-xl hover:border-orange-400 hover:bg-orange-50/50 transition-all disabled:opacity-50"
                >
                  <div className="flex items-center min-w-0">
                    <div className="bg-gradient-to-br from-orange-500 to-amber-500 p-2.5 rounded-lg mr-3 flex-shrink-0">
                      <Building2 className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-left min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{org.name}</p>
                      {org.slug && <p className="text-xs text-gray-500 truncate">elevante.ai/{org.slug}</p>}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-orange-500 transition-colors flex-shrink-0 ml-3" />
                </button>
              ))}
            </div>
            <button
              onClick={goBackToEmail}
              className="w-full text-center text-sm text-gray-600 hover:text-gray-800 mt-2 py-2"
              data-testid="login-org-picker-back"
            >
              ← Use a different email
            </button>
          </div>
        )}

        {/* Footer links removed per product decision — no public org creation/joining. */}
      </div>
    </div>
  );
}

export default LoginPage;
